import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, RefreshCw, Trash2, Shield, List } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import PageBreadcrumb from '../common/PageBreadcrumb';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast } from '../common/Toast';
import complianceService from '../../services/complianceService';

const FrameworkList = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [frameworks, setFrameworks] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [frameworkToDelete, setFrameworkToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadFrameworks = async () => {
    setLoading(true);
    try {
      const response = await complianceService.getFrameworks();
      setFrameworks(response.data || []);
    } catch (err) {
      console.error(err);
      showError('Failed to load frameworks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFrameworks();
  }, []);

  const handleDeleteClick = (framework) => {
    setFrameworkToDelete(framework);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!frameworkToDelete) return;
    
    setIsDeleting(true);
    try {
      await complianceService.deleteFramework(frameworkToDelete._id || frameworkToDelete.id);
      success('Framework deleted successfully');
      setShowDeleteModal(false);
      setFrameworkToDelete(null);
      loadFrameworks(); // Refresh list
    } catch (err) {
      showError(err.message || 'Failed to delete framework');
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Framework Name',
      width: '25%',
      render: (value, row) => (
        <div>
          <div className="fw-bold text-dark">{value}</div>
          <small className="text-muted">{row.code}</small>
        </div>
      )
    },
    {
      key: 'type',
      label: 'Type',
      width: '15%',
      render: (value) => (
        <span className="badge bg-light text-dark border">{value}</span>
      )
    },
    {
      key: 'description',
      label: 'Description',
      width: '40%',
      render: (value) => <span className="text-truncate d-block" style={{maxWidth: '300px'}}>{value}</span>
    },
    {
      key: 'requirements',
      label: 'Controls',
      width: '10%',
      render: (value) => (
        <span className="badge bg-info bg-opacity-10 text-info">
          {value ? value.length : 0} Requirements
        </span>
      )
    }
  ];

  const headerActions = [
    {
      icon: <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />,
      onClick: loadFrameworks,
      variant: 'outline'
    },
    {
      label: 'New Framework',
      icon: <Plus size={18} />,
      onClick: () => navigate('/contracts/frameworks/new'),
      variant: 'primary'
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Regulatory Frameworks" 
        description="Manage compliance standards (GDPR, ISO, SOX) and their specific requirements."
        actions={headerActions}
      />

      <div className="p-6">
        <div className="bg-white border rounded-lg shadow-sm">
          <DataTable
            data={frameworks}
            columns={columns}
            loading={loading}
            searchable={true}
            pagination={true}
            emptyMessage="No frameworks defined yet."
            showActions={true}
            onEdit={(row) => navigate(`/contracts/frameworks/edit/${row._id}`)}
            onDelete={handleDeleteClick}
          />
        </div>
      </div>
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Framework"
        message={`Are you sure you want to delete "${frameworkToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />
    </>
  );
};

export default FrameworkList;